module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('div', {
    staticClass: "chapter",
    attrs: {
      "id": "ch02"
    }
  }, [_c('h1', {
    staticClass: "ct"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p12"
    }
  }), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/Text/Hardesty0006#toc"
    }
  }, [_vm._v("1. Origins")])]), _vm._v(" "), _c('p', {
    staticClass: "pf"
  }, [_vm._v("Boston, a slave who belonged to James Gardner and who was on his deathbed in 1761, accused Quaco, another slave, of poisoning him a month earlier. Although Boston later died, William Stoddard, the justice assigned to investigate the matter, believed Boston’s allegation alone was not enough to convict Quaco. The judge decided to dig deeper into Quaco’s past, and upon further investigation, he learned Quaco was from the Dutch colony of Suriname on the northern coast of South America. A plantation colony specializing in sugar, coffee, cocoa, and cotton production and heavily dependent on slave labor, Suriname was a regular trading partner with the New England colonies. Boston merchants sold dried fish, agricultural products, and manufactured goods for molasses and other commodities. Occasionally, as the case of Quaco demonstrates, merchants purchased a few slaves in the Dutch colony and brought them back to Boston to sell. Stoddard, aware of this trade, approached a ship captain, Duncan Ingraham, who knew Quaco’s Surinamese master. According to Ingraham, Quaco’s former master, a Mr. Felix, sold the slave to Captain John Fraiser of Boston, and Ingraham “never heard that said Quaco was confined for poisoning or any other crime at Suriname to Occasion his being sent off.” Quaco was a good slave, according to Ingraham, but another witness did not share Ingraham’s optimistic conclusion."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en22",
      "id": "en22r"
    }
  }, [_vm._v("1")])]), _c('a', {
    attrs: {
      "id": "anchor29"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("On 19 September 1761, shortly after Boston’s death, Stoddard summoned Arnold Wells’s slave named Boar, whose name was possibly a corruption of the Dutch "), _c('i', [_vm._v("boer")]), _vm._v(", meaning “farmer.” Like Quaco, "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p13"
    }
  }), _vm._v("Boar was from Suriname; he remembered Quaco from his time there and even had a family connection. According to Boar, he knew the accused poisoner because his mother and Quaco were imprisoned together “on account of poison.” While Boar’s mother was executed for the crime, once Boar arrived in Boston, he often heard Quaco say he was only “sent away” for the crime. Unfortunately, we do not know the outcome of Stoddard’s investigation or if Boar’s testimony was important in convicting Quaco. More importantly, however, Quaco had a past, one deeply embedded in Atlantic slavery that did not disappear when he arrived in Boston.")]), _vm._v(" "), _c('p', [_vm._v("While Quaco’s past came back to haunt him in a deeply personal way, all of Boston’s slaves carried both their own experiences and larger cultural values with them when they arrived in New England. To better understand the world of Boston’s slaves, we need to analyze these origins, especially their relationship with slavery. Eighteenth-century slaves in Boston were the descendants of a small native-born black population that had been in New England since the 1630s; American-born creoles or Africans who had resided in the Americas for a long period of time; or native Africans mostly, but not exclusively, from West Africa, including the Senegambia region and the Gold Coast. Slavery was deeply entrenched in each of these places, meaning that all Afro-Bostonians had encountered and had experience with slavery as an institution. Although the slave system they came from could be very different from the one they entered, they nevertheless had experience with commodification and unfreedom, allowing them to find more effective ways to adapt to their new homeland."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en23",
      "id": "en23r"
    }
  }, [_vm._v("2")])]), _c('a', {
    attrs: {
      "id": "anchor30"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Black Bostonians also had prior experiences with other institutions, such as Islam in the case of Senegambian slaves, which helped slaves navigate white society in Boston. All of this accumulated cultural knowledge from New England, Africa, and the Caribbean came together as a result of Boston’s involvement in the slave trade. By the first quarter of the eighteenth century, the town’s enslaved population was expanding and diverse. These slaves, equipped with knowledge of slavery and cultural traditions that allowed them to adapt to colonial conditions, had the flexibility and ability to integrate themselves into Euro-American society.")]), _vm._v(" "), _c('p', [_vm._v("By examining the origins of Boston’s slaves, we clearly see that the slave trade was an engine of Atlantic diaspora, shuffling enslaved populations to areas all around its littoral. Yet its significance was not simply about the movement of people to labor on distant shores but "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p14"
    }
  }), _vm._v("about the values, traditions, and knowledge those populations carried with them. Most important was the shared experience of slavery, as many slaves moved from one slave society to another. They accumulated knowledge on how to best navigate and contest enslavement and later employed that information once they arrived in Boston, eventually channeling their energies into acquiring concrete material gains and creating a space for themselves in Euro-American society.")]), _vm._v(" "), _c('p', {
    staticClass: "psec"
  }, [_vm._v("The first group that composed Boston’s eighteenth-century slave population was the relatively small group of Afro–New Englanders that had been in the region since the 1630s. Descendants of African-born slaves who most likely spent some of their time in the Caribbean, this population hovered around one thousand people for all of New England during the seventeenth century."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en24",
      "id": "en24r"
    }
  }, [_vm._v("3")])]), _vm._v(" By 1700, about eight hundred of these people lived in Massachusetts, and it is safe to assume that two to three hundred lived in Boston, the colony’s largest urban center and most diverse economy."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en25",
      "id": "en25r"
    }
  }, [_vm._v("4")])]), _vm._v(" Although few in number, black New Englanders were fixtures in the social fabric of the region and constituted one of many classes of the unfree, which included indentured servants, apprentices, and Indian slaves. Like their descendants in the eighteenth century, they created multiracial communities and learned to navigate local institutions. Nevertheless, the relatively low density of slaves meant they did not reproduce at the prodigious rates of their white contemporaries, and their population always had to be reinforced with new arrivals."), _c('a', {
    attrs: {
      "id": "anchor31"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor32"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("In regard to slavery, seventeenth-century New England was quite similar to other seventeenth-century mainland English colonies such as New York and Virginia. Even in the latter case, Virginia was a not a full-fledged slave society until the passage of the Slave Code of 1705."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en26",
      "id": "en26r"
    }
  }, [_vm._v("5")])]), _vm._v(" In Virginia, however, the slave code was not comprehensive and left many issues unresolved. About the only place where slavery had been organized in any systematic way was the Caribbean, where the sugar colony of Barbados passed an often-copied slave code in 1661."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en27",
      "id": "en27r"
    }
  }, [_vm._v("6")])]), _vm._v(" As the early eagerness to adopt a slave code suggests, Barbados and the other English West Indian colonies were the centers of slavery in the Anglophone world. Given the close economic and social relationship between New England merchants and Caribbean planters, seventeenth-century slavery in Boston and the rest of the region was an extension of this relationship. In the course of trade and other forms of contact, New England and the West Indies became “one economic "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p15"
    }
  }), _vm._v("region,” and a crucial link between the two “involved slave labor.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en28",
      "id": "en28r"
    }
  }, [_vm._v("7")])]), _vm._v(" This relationship made New England not only a Puritan errand in the wilderness but also a slave-owning society."), _c('a', {
    attrs: {
      "id": "anchor33"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor34"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor35"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Of course, New Englanders’ close relationship with the West Indies did not force them to enslave Africans or to legally define slavery as an institution: it was a conscious decision. They had no problem enslaving Native Americans. After the Pequot War in 1636, they traded some of the war captives for African slaves in the Caribbean, who arrived in Boston in 1638, beginning the region’s long history with African slavery. Three years later, settlers in Massachusetts made an attempt to govern slavery when they passed the colony’s first legal code, "), _c('i', [_vm._v("The Body of Liberties")]), _vm._v(". Article 91 ambiguously stated, “There shall never be any bond slaverie, villange or Captivitie amongst us, unless” someone captured those slaves in just wars, those slaves were “strangers” who sold themselves into slavery, or someone else captured, enslaved, and sold them to the colonists. Adding another layer of ambiguity, these slaves were to “have all the liberties and Christian usages which the law of god established in Israell concerning such persons doeth morally require.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en29",
      "id": "en29r"
    }
  }, [_vm._v("8")])]), _vm._v(" At first glance, this clause seems to be a negative, declaring slavery illegal in all but three cases, yet it also opened the door to racial slavery. Most captives taken in so-called just wars would have been Native Americans captured in the vicious Indian wars of the 1630s. Strangers, on the other hand, primarily meant one group of people: Africans. While not many people would have sold themselves into slavery, except perhaps a few destitute English men and women looking for a better life abroad, most of the strangers sold to Massachusetts would have been blacks from the Caribbean and Africa. As for the sentence concerning ancient Israel, it is unclear if men and women could sell their daughters into slavery as suggested in Exodus, although masters probably liked the clause that considered all enslaved “strangers” (Leviticus 25:44–46) as permanent, inheritable property. The loopholes in the 1641 "), _c('i', [_vm._v("Body of Liberties")]), _vm._v(" allowed for the existence of racial slavery and possibly created chattel slavery."), _c('a', {
    attrs: {
      "id": "anchor36"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Despite the backhanded legalization of slavery in article 91, the legal status of African slaves remained relatively ambiguous throughout the seventeenth century. It seems some masters considered slaves to be indentured servants. By comparing the wills of three seventeenth-century New Englanders, we can see this process. When the Boston merchant Antipas Boyse was composing his last will and testament on 3 July 1669, he took time to consider the fate of his slave Janemet. "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p16"
    }
  }), _vm._v("After two additional years of service to Boyse’s heirs, Janemet would be free, provided he served his masters “faithfully.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en30",
      "id": "en30r"
    }
  }, [_vm._v("9")])]), _vm._v(" Unlike Boyse, who owned African slaves, George Alcock of Roxbury had two white servants, John Plimton and Joseph Wise. Like Janemet, both Wise and Plimton received their freedom shortly after Alcock’s death, this time “after midsomer next.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en31",
      "id": "en31r"
    }
  }, [_vm._v("10")])]), _vm._v(" Other slave owners gave their slaves their freedom shortly after their death as well. Mary Smith, the widow of Bostonian Abraham Smith, stated in her will why she freed her slaves Susan and Maria: for their “good care & diligence of me & my lat Husband.” Nevertheless, the enslaved women still had to behave themselves for the remainder of Mary’s life; otherwise “this Deed of Gift” would “be frustrate void & of none effect,” indicating their freedom was provisional and not guaranteed."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en32",
      "id": "en32r"
    }
  }, [_vm._v("11")])]), _vm._v(" Yet conditional freedom was not exclusive to African slaves. Alcock made Plimton pay five pounds to be freed along with Wise."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en33",
      "id": "en33r"
    }
  }, [_vm._v("12")])]), _vm._v(" Although colonists bought, sold, and passed slaves on to their heirs, many seem to have considered their obligation to serve to be finite and, much like white servants, not lifelong."), _c('a', {
    attrs: {
      "id": "anchor37"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor38"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor39"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor40"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("The status of the children of enslaved Africans in seventeenth-century Boston was also problematic. English common law long held the notion of "), _c('i', [_vm._v("partus sequitur patrem")]), _vm._v(", a belief that children took the condition of their fathers. For slave owners looking to increase the number of bondsmen and bondswomen they owned, this was not an issue if the father was a slave. Enslaved women who bore the children of free men, however, proved to be especially problematic for masters—who were sometimes the fathers. Virginian slaveholders solved this problem in 1662, eschewing English law and adopting the Roman law principle of "), _c('i', [_vm._v("partus sequitur ventrem")]), _vm._v(", meaning children took the status of the mother. Interestingly, none of the New England colonies ever adopted this law, but as a historian noted, “custom and tradition achieved the same end.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en34",
      "id": "en34r"
    }
  }, [_vm._v("13")])]), _vm._v(" Even in the eighteenth century, Massachusetts failed to pass a law clarifying the condition of enslaved children, leaving their slave status open to question."), _c('a', {
    attrs: {
      "id": "anchor41"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Adding another layer of ambiguity, the seventeenth-century labor market was not always amicable to slavery. In early November 1661, the selectmen of Boston investigated Thomas Deane, who “employed a Negro in ye manufacture of a Coop.” According to the town leaders, Deane’s actions were “contrary to ye orders of ye Towne,” suggesting the selectmen frowned on the use of slave labor in skilled trades. They then issued a cease-and-desist order, commanding that Deane “shall "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p17"
    }
  }), _vm._v("not employ ye sd Negro in ye sd manufacture as a Coop or any other manufacture or science after ye 14th day of this month.” If the slave continued making barrels, his master would be given a “penalty of 20s, for euery day yt ye sd Negro shall continue in such employment.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en35",
      "id": "en35r"
    }
  }, [_vm._v("14")])]), _vm._v(" Beyond official restraint regarding slave labor, it seems most enslaved workers in seventeenth-century New England either provided farm labor in the rural areas or worked as domestics or valets in their masters’ homes."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en36",
      "id": "en36r"
    }
  }, [_vm._v("15")])]), _vm._v(" Compared to the eighteenth century, when slave labor was an integral part of all sectors of Boston’s economy, especially the artisanal trades, the selectmen’s reprimand of Deane and the limited use of slaves in all parts of the economy demonstrate there was at least some hesitancy to employ slave labor."), _c('a', {
    attrs: {
      "id": "anchor42"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor43"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("While an uncertain labor market helped to confuse an already ill-defined institution, much of the ambiguity may have had to do with the behavior of the slaves themselves. Given the low population density of African slaves in colonial New England—by 1700, there were ninety thousand inhabitants in New England, only one thousand of whom were slaves—many slaves attempted to integrate themselves into colonial society."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en37",
      "id": "en37r"
    }
  }, [_vm._v("16")])]), _vm._v(" This was especially true in Boston, a crowded cosmopolitan port town where Africans could easily mingle with whites, Native Americans, and other Africans. Consider Will, a slave belonging to Captain Prentice. In 1700, Will died after falling off a horse. Although dead, Will was not forgotten. Puritan magistrate Samuel Sewall took time in his diary to commemorate Will, a man Sewall obviously thought to be part of the community. Sewall remembered the slave “much delighted in Horses,” and the irony was not lost on the justice that Will “now dies by a horse.” Beyond Will’s love of all things equine, Sewall also recalled an incident from nearly forty years before, in 1664. In that year, Will saved his “Master Prentice from a Bear.” Will and Sewall had also spent considerable time together, as the bondsman traveled with the judge and Colonel Townsend to Albany, New York."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en38",
      "id": "en38r"
    }
  }, [_vm._v("17")])]), _vm._v(" Nevertheless, while Will was obviously trusted and respected by Sewall, the magistrate’s depiction of the slave suggests an obedient, docile paragon of service. Will’s slavery lasted at least forty years, and his only memorial did not mention Will’s friends, family, or community, only Sewall’s."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en39",
      "id": "en39r"
    }
  }, [_vm._v("18")])]), _vm._v(" Whether Will was as compliant as Sewall suggests will never be known, but his very mention in Sewall’s diary suggests a slave ingrained in the community."), _c('a', {
    attrs: {
      "id": "anchor44"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor45"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor46"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Slaves enmeshed themselves in white society in other ways as well. Seventeenth-century church records are full of enslaved people "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p18"
    }
  }), _vm._v("presenting themselves or their children to be baptized. The First Church of Boston recorded baptizing “William or William and Hannah Negro” in 1682 and “Lydia Pollow a negro Child” in 1697."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en40",
      "id": "en40r"
    }
  }, [_vm._v("19")])]), _vm._v(" Likewise, John Winthrop, founder of the Bay Colony, noted in his diary in 1641, a “negro maid, servant to Mr Stoughton, of Dorchester, being well approved by divers years’ experience, for sound knowledge and true godliness, was received into the church and baptized.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en41",
      "id": "en41r"
    }
  }, [_vm._v("20")])]), _vm._v(" This enslaved woman received baptism only three years after the first slaves arrived in Boston, indicating that either slaves quickly learned the importance of religion in Puritan society, she had been exposed to Christianity before her arrival in Massachusetts, or some combination of the two."), _c('a', {
    attrs: {
      "id": "anchor47"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor48"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Another indicator of how embedded slaves were in the social world of Boston was the frequency of interracial sexual liaisons. Court records attest to this trend. As fornication was a crime, interracial couples constantly appeared before the court, sometimes for sexual acts and at other times for having children out of wedlock. In late October 1672, the court found the English servant Christopher Mason guilty of “getting Mr. Rock’s Negroe maide Bess with Childe.” Mason received twenty lashes and had to pay a fine for his transgression. Bess, who appeared three months later, after having the “illegitimate Childe,” received a whipping, and the court forced her to pay all legal fees."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en42",
      "id": "en42r"
    }
  }, [_vm._v("21")])]), _vm._v(" Eight years later, Kathalina, a slave belonging to Thomas Dewer, accused Loftlan Loney of being the father of a “Bastard Childe born of her body about a month agone.” Soon after the proceedings against Loney began, the child died. This tragedy became Loney’s saving grace, as the court decided not to pursue the matter any further, knowing Loney would not stop “denying of it,” and only forced him to pay court costs. Kathalina’s ordeal was not over, however. Since she had approached the court claiming Loney was the father of her bastard child, she had also inadvertently confessed to fornicating, receiving fifteen lashes and a forty-shilling fine for her troubles."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en43",
      "id": "en43r"
    }
  }, [_vm._v("22")])]), _vm._v(" These relations involved not only Africans and whites but also Africans and Indians. A Mr. Warren of Boston owned two slaves, one African and one Native American. In the same year as the case involving Bess and Mason, Jasper, the Indian slave, confessed to “committing Fornication” with Joan the African."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en44",
      "id": "en44r"
    }
  }, [_vm._v("23")])]), _vm._v(" Not only do these fornication cases demonstrate how close slaves were with other Bostonians, but they also suggest that enslaved Africans associated with people of a similar social standing. Indeed, two of the three men involved in these cases "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p19"
    }
  }), _vm._v("were lower class. Mason was an indentured servant, and Jasper an Indian. While elites like Samuel Sewall could memorialize slaves they deemed worthy, it seems the enslaved were more comfortable interacting with people of a similar status."), _c('a', {
    attrs: {
      "id": "anchor49"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor50"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor51"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Perhaps the most important part of slaves’ integrating themselves into colonial society was their ability to learn the law. Most of this legal knowledge was experiential. As the fornication suits illustrate, despite the low numbers of slaves, they frequently appeared before the courts. In the 1670s, a slave woman named Hannah appeared before Suffolk County justices for stealing surgical equipment from Daniel Stone and selling it to Mary Pittum. The court found Hannah guilty and sentenced her to be whipped ten times and pay Stone ten pounds for the missing implements. Hannah accepted the lashes as just punishment but appealed the fine. On appeal, Hannah sought to defend herself under the “Law titell buglery theft page :13 section 2,” stating that all fines incurred by servants and children would not be paid and that dependents could only be “openly punished,” meaning corporal punishment. Since Hannah had already been whipped for her crime, she claimed she did not have to pay the fine. Although the court did not buy Hannah’s argument and she lost her appeal, her understanding of the law was detailed and precise, especially given that she may have been illiterate—demonstrated by her signing the court documents with a mark."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en45",
      "id": "en45r"
    }
  }, [_vm._v("24")])]), _vm._v(" How Hannah learned the law and how exemplary she was are both unknown, but it is safe to assume slaves’ knowledge of the law was not as extensive as in the next century."), _c('a', {
    attrs: {
      "id": "anchor52"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("By the beginning of the eighteenth century, there was a small native population of slaves in Boston. In some ways, such as their attempts to integrate themselves into the community at large, this enslaved population resembled its eighteenth-century counterparts. In other ways, especially with respect to their role in the labor market, they could not have been more different. Moreover, slavery as an institution was legally ambiguous and ill defined. Legislation regarding slavery was sparse or, in the case of determining the status of slaves’ children, nonexistent. Nevertheless, by 1700, slavery was firmly entrenched in Boston and was a customary institution, and new arrivals from the Caribbean and Africa became part of this slave society.")]), _vm._v(" "), _c('p', {
    staticClass: "psec"
  }, [_vm._v("The new arrivals came in unprecedented numbers. Before considering the other two sources of enslaved Bostonians, it is important to examine the processes that brought black slaves to Boston and the "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p20"
    }
  }), _vm._v("demographic transformation they wrought. One of the biggest factors in bringing Africans to Massachusetts was the opening of the transatlantic slave trade to all interested parties. Beginning in 1660 with the Restoration of Charles II, a group of English merchants received a monopoly on slave trading. Their company, known as the Royal African Company, was notoriously inefficient and corrupt, depriving most peripheral regions of the English empire, including the Carolinas, Virginia, and New England, of slaves. The Royal African Company’s monopoly affected how many slaves arrived in the Americas and the manner in which they arrived there."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en46",
      "id": "en46r"
    }
  }, [_vm._v("25")])]), _vm._v(" In 1680, Massachusetts governor Simon Bradstreet wrote to the Board of Trade and Plantations in England explaining there “hath been no Company of blacks or Slaves brought into the Country since the beginning of this plantation.” In nearly fifty years of being settled, only “one small Vessell about two years” before Bradstreet wrote the letter, “brought hither betwixt Forty and fifty Negro’s” to the colony. Instead of receiving full coffles, “Now and then, two or three Negro’s are brought hither from Barbados and other of his Majesties plantations.” Bradstreet estimated that this ad hoc process of acquiring slaves brought “about one hundred or one hundred and twenty” slaves to the colony, a relatively low estimate."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en47",
      "id": "en47r"
    }
  }, [_vm._v("26")])]), _vm._v(" Although Boston merchants occasionally attempted to skirt the monopoly and chartered their own voyages to buy slaves on the African coast, their attempts never brought more than a few slaves into the region."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en48",
      "id": "en48r"
    }
  }, [_vm._v("27")])]), _c('a', {
    attrs: {
      "id": "anchor53"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor54"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor55"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Following the fall of Charles II’s brother James II during the Glorious Revolution, however, the Royal African Company lost its monopoly, and the slave trade opened to any merchant in the empire with the capital to charter a voyage. Bostonians were not big participants in the direct trade to Africa, but they did send at least thirty voyages during the period under study."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en49",
      "id": "en49r"
    }
  }, [_vm._v("28")])]), _vm._v(" It must be noted, however, that rarely did these voyages bring a full shipload of slaves to Boston. While eighteenth-century white Bostonians were labor hungry, the market for slaves was never as large as that in the Caribbean or southern colonies. Ships from Boston would buy slaves in Africa and sell them in the West Indies, South Carolina, or Virginia. The leftovers would be sold in Boston. Many people made special requests to ship captains for slaves. Peter Faneuil, one of the wealthiest men in Boston and whose ship, the "), _c('i', [_vm._v("Jolly Bachelor")]), _vm._v(", was a fixture in the Boston slave trade, asked the captain of one of his vessels, Peter Buckley, to bring him “for the use of [his] house,” a “strait limbed Negro lad as possible "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p21"
    }
  }), _vm._v("[Buckley] can about the age of from 12 to fiveteen years, & if to be done one that has had the small pox.” The slave, “being for [Faneuil’s] Own service,” must “be one of as tractable a disposition as [Buckley] can find.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en50",
      "id": "en50r"
    }
  }, [_vm._v("29")])]), _vm._v(" More important for the direct trade from Africa to New England were merchants in Rhode Island, the center of colonial slave trading."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en51",
      "id": "en51r"
    }
  }, [_vm._v("30")])]), _vm._v(" Bostonians often took the journey to Rhode Island to purchase slaves. The jurist Nathaniel Byfield in his will remembered purchasing his slave Rose in Bristol after she arrived from the West Indies in 1718, when she was about thirteen years old."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en52",
      "id": "en52r"
    }
  }, [_vm._v("31")])]), _vm._v(" While Boston was still not a preferred market for even its own merchants, the opening of the slave trade to all capable parties dramatically increased the number of slaves available."), _c('a', {
    attrs: {
      "id": "anchor56"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor57"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor58"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor59"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Meanwhile, the older ad hoc process of merchants buying slaves in the Caribbean, especially Barbados, and selling them in Boston continued, only on a greater scale. Given the extensive commerce between Massachusetts and the West Indies, it is not surprising that slaves were also bought there and brought to Boston along with cargos of molasses and sugar. In the eighteenth century, however, slaves came to constitute a larger volume, especially in monetary value, of Caribbean trade. Hugh Hall, the son of a Barbadian planter and graduate of Harvard College who split his time between Barbados and Boston, ran an import/export business selling manufactured goods, fish, and timber from New England in exchange for sugar and slaves. Hall documented all of these transactions in his account book, but in 1729, the merchant attempted to record all “Negro’s Received from Barbados,” including ones he did not buy or sell. According to his estimations, Bostonians bought eighty slaves from the island in that year. A cursory glance at the names of the slaves listed suggests a couple of things. First, African names such as “Quaco,” “Quashey,” and “Bonabah” appear beside English names such as “Bess,” “Betty,” and “Tom” and the derogatory classical and biblical names that masters often gave slaves, like “Dido” and “Jupiter.” This group of slaves may have been a mix of African-born slaves, who either were immediately shipped to Boston via Barbados or had only spent a short time in the plantation colony, and American-born creoles, whose masters gave them European names."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en53",
      "id": "en53r"
    }
  }, [_vm._v("32")])]), _vm._v(" Not only does this mixture of origins make it difficult to determine where enslaved Bostonians came from, but it is also testament to the sheer diversity of slaves being brought into the town."), _c('a', {
    attrs: {
      "id": "anchor60"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Hall’s account book may be an artifact of diversity, yet it also taps into a much larger trend: the demographic changes brought about "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p22"
    }
  }), _vm._v("by such a wide-scale importation of slaves. As mentioned before, the black population in Massachusetts was 800 in 1700, but by 1776, there were 5,249 people of African descent in Massachusetts, a 650 percent increase. Although blacks only made up 1.8 percent of the population of Massachusetts, their demographic footprint in Boston was much greater and only grew during the eighteenth century."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en54",
      "id": "en54r"
    }
  }, [_vm._v("33")])]), _vm._v(" In 1704, there were 400 slaves living in Boston, while in 1752, there were 1,541 slaves, an increase of over 350 percent."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en55",
      "id": "en55r"
    }
  }, [_vm._v("34")])]), _vm._v(" In that latter year, there were 15,730 inhabitants of Boston, meaning African slaves made up nearly 10 percent of the town’s population."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en56",
      "id": "en56r"
    }
  }, [_vm._v("35")])]), _vm._v(" Yet that percentage may be low for a number of reasons. Slaves were taxable property, meaning it behooved masters to hide their chattel from census takers. Likewise, census records provide only a static picture of Boston’s black population. Court records and other documents suggest a much more fluid and mobile population. Town officials commonly told “strangers” to leave Boston, a process known as “warning out.” While most of the African interlopers tended to be free blacks, there are a number of slaves in the records. Most of the enslaved people sent out were from surrounding towns, as was a slave named Charlestown, who was unsurprisingly from the town of Charlestown across the Charles River from Boston."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en57",
      "id": "en57r"
    }
  }, [_vm._v("36")])]), _vm._v(" Like the slave Charlestown, there seems to have been a large number of slaves from surrounding towns who came into Boston for recreation or to work, run errands for their masters, find a mate, or visit a loved one who lived in the colony’s capital. Finally, given the large number of enslaved sailors who traversed the Atlantic world, almost every ship that came into port would either return enslaved Bostonians home or bring new ones into port. The black population of Boston grew dramatically during the eighteenth century, and although it is next to impossible to determine the exact number of slaves in Boston at any given time, it is safe to assume it was larger than what colonial censuses claimed."), _c('a', {
    attrs: {
      "id": "anchor61"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor62"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor63"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor64"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "psec"
  }, [_vm._v("Slaves who had spent considerable amounts of time or were born in other parts of the Americas, especially the West Indies and the American South, were an important component of this demographic transformation. As Hugh Hall’s account book indicates, it is hard to discern which slaves were born in the Americas or were only transshipped from there. Nevertheless, on the basis of newspaper ads, we know that some slaves were born in the Caribbean and other places and sent to Boston. In November 1712, the "), _c('i', [_vm._v("Boston News-Letter")]), _vm._v(" ran an "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p23"
    }
  }), _vm._v("advertisement for a “Young negro girl born in Barbadoes that speaks good English.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en58",
      "id": "en58r"
    }
  }, [_vm._v("37")])]), _vm._v(" The latter part of this ad indicates why American-born slaves were in such high demand. They could speak European languages, usually English and sometimes multiple others, making it easier for masters to command their chattel and teach them profitable skills. Likewise, they would have been familiar with how slavery functioned in British society, and potential owners believed them to be more docile. Even those who had spent a short time in the Americas were preferable to slaves fresh from Africa, as their exposure to Euro-American society was thought to make them better slaves. The slaves themselves, however, were trapped between two worlds, an African past and an American present, and used both knowledge sets to navigate eighteenth-century Boston."), _c('a', {
    attrs: {
      "id": "anchor65"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("By examining the lives of two slaves before they were sold away from the Caribbean to Boston, we will be able to see the slavery they experienced and the social worlds they inhabited before arriving in the place where they spent their final years. One slave, Mark, who we will meet again in the next chapter, belonged to Captain John Codman, a wealthy merchant from Charlestown. The second slave, Quaco, was already introduced at the beginning of this chapter. Both Mark and Quaco had Caribbean roots, Mark being born and spending his childhood in Barbados and Quaco residing for some time in Dutch Suriname. Both slaves had firsthand experiences of West Indian plantation society, giving them a deep understanding of slavery as an institution, their degraded place in European New World societies, and ways to resist that degradation.")]), _vm._v(" "), _c('p', [_vm._v("“I was born to a reputable family” in Barbados in 1725, Mark told those who witnessed his execution in the summer of 1755, and left his “native Place very Young” and “came to Boston.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en59",
      "id": "en59r"
    }
  }, [_vm._v("38")])]), _vm._v(" It makes one wonder if Mark, having just been convicted of murdering his master and sentenced to death, thought about his childhood and his parents, friends, and acquaintances in his tropical homeland. Maybe his memories were just a faint glimmer in his mind, but it is hard to imagine he forgot everything about growing up in Barbados. By his own description, he was “very Young” when he arrived in New England, but masters in Boston needed workers, not more children to raise, suggesting that Mark was probably between the ages of six and ten when he arrived, about the age boys went to work. Despite the short time living in the island colony, Mark’s experience would have been formative."), _c('a', {
    attrs: {
      "id": "anchor66"
    }
  })]), _vm._v(" "), _c('p', [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p24"
    }
  }), _vm._v("To better understand why Mark’s childhood in Barbados was so important for his later life, we need to better understand the history of slavery on the island. Barbados was one of the earliest colonies settled by the English, with settlement beginning in 1625. Although its white population grew steadily over the next two decades, its economy did not take off until the 1640s with the advent of sugar cultivation. Rather quickly, wealthier white Barbadians began to consolidate their ownership of the land and replace white indentured servants and free laborers with African slaves."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en60",
      "id": "en60r"
    }
  }, [_vm._v("39")])]), _vm._v(" Less than twenty years later, blacks outnumbered whites on the island, and by 1680, they outnumbered European colonists by more than two to one."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en61",
      "id": "en61r"
    }
  }, [_vm._v("40")])]), _vm._v(" While almost all of these slaves were native Africans during the first decades of slavery, by the early eighteenth century, a small native black population began to appear. Mark, a member of this creole class, and others like him indicated, according to the historian Jennifer Morgan, the “growing ability of men and women to navigate the social terrain of the island.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en62",
      "id": "en62r"
    }
  }, [_vm._v("41")])]), _vm._v(" Being able to successfully reproduce themselves, these enslaved pioneers produced children who spoke English and came to be trusted by the master class. Owners elevated them to positions above African plantation workers and insulated them (to a certain extent) from the everyday violence and depravity of plantation slavery; many of the male creoles became artisans, fulfilling specialized labor needs. With this special treatment also came mobility."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en63",
      "id": "en63r"
    }
  }, [_vm._v("42")])]), _vm._v(" Mark, then, was on track to become a skilled slave with a great degree of autonomy and perhaps observed adult slaves in a similar situation successfully navigate Barbadian slavery to obtain some level of material comfort. Nevertheless, Mark’s ability to mitigate his slave status was something that came to fruition in Boston, not Barbados."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en64",
      "id": "en64r"
    }
  }, [_vm._v("43")])]), _vm._v(" Why Mark’s master would have sold away such a valuable slave is unknown, although there was no guarantee Mark, being a boy, would survive to adulthood and become a valued member of plantation society."), _c('a', {
    attrs: {
      "id": "anchor67"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor68"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor69"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor70"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor71"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Despite Mark’s creole status and all the benefits that came with it, he still would have had ties to his African ancestry. The bulk of slaves in Barbados were from Africa, and the miserable working conditions and disease environment meant that the slave population actually declined at a rate of about 5 percent a year."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en65",
      "id": "en65r"
    }
  }, [_vm._v("44")])]), _vm._v(" Some masters even preferred to buy new slaves rather than allow their current chattel to reproduce, as, according to one planter, “it was cheaper to work slaves to the utmost, and by little fare and hard usage, to wear them out before they became useless and then to buy new ones.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en66",
      "id": "en66r"
    }
  }, [_vm._v("45")])]), _vm._v(" This meant all "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p25"
    }
  }), _vm._v("of the slaves on the island, native born or African, constantly encountered new arrivals who brought African cultural and social practices with them. As Griffith Hughes, the author of an eighteenth-century natural history of Barbados, noted, the “Negroes in general are very tenaciously addicted to the Rites, Ceremonies, and Superstitions of their own Countries, particularly in their Plays, Dances, Music, Marriages, and Burials.” “And even such as are born and bred up here,” Hughes added, “cannot be intirely ["), _c('i', [_vm._v("sic")]), _vm._v("] weaned from these Customs.” All of the slaves stood “much in Awe of such as pass for "), _c('i', [_vm._v("Obeah")]), _vm._v(",” blacks who were essentially spiritual doctors and who “cured” slaves “when they [were] bewitched by others.” In addition to the nearly universal belief in the supernatural, most Africans also liked to adorn themselves with “Strings of Beads of various colors.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en67",
      "id": "en67r"
    }
  }, [_vm._v("46")])]), _vm._v(" Even in death, African slaves could not escape their cultural traditions, believing when they died that “they shall return to their own Country.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en68",
      "id": "en68r"
    }
  }, [_vm._v("47")])]), _vm._v(" Although it is uncertain if creoles like Mark believed they returned to Africa upon death, they were nevertheless the product of two different cultural traditions, one that empowered them against slavery and another that allowed them to be part of the wider slave community. Both of these equipped Mark to decode the ambivalent and complicated world of slavery, skills he brought with him when he traveled to Boston."), _c('a', {
    attrs: {
      "id": "anchor72"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor73"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor74"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor75"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Unlike Mark, Quaco arrived in Boston as an adult after spending a considerable amount of time living in the Dutch colony of Suriname. Why he was an attractive investment to any English person, especially given his reputation as a poisoner, is unknown, but the peculiar history of Suriname offers clues. The colony, located on the northeastern coast of South America and also known as the Wild Coast, had been a contested zone between the British, French, Spanish, Portuguese, and Dutch empires since the late sixteenth century. A number of colonial powers had tried to settle the area, including the English, who established a colony there in the 1630s and, after its failure, another in the 1650s. English control of the region did not last long, and the Dutch conquered the colony in 1667."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en69",
      "id": "en69r"
    }
  }, [_vm._v("48")])]), _vm._v(" The Netherlanders quickly turned the colony into a productive center of sugar cultivation, and by 1700, the one thousand white settlers were heavily outnumbered by eighty-five hundred people of African descent."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en70",
      "id": "en70r"
    }
  }, [_vm._v("49")])]), _vm._v(" Despite the Batavianization of the colony, however, the slaves continued to speak a dialect of English. Almost a century after the Dutch conquest, the botanist Edward Bancroft noticed, “at this time a species of corrupt "), _c('i', [_vm._v("English")]), _vm._v(" is universally spoken by the Negroes.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en71",
      "id": "en71r"
    }
  }, [_vm._v("50")])]), _vm._v(" In fact, to this day, the lingua franca spoken "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p26"
    }
  }), _vm._v("in Suriname, Sranan, is derisively called "), _c('i', [_vm._v("Neger Engelsche")]), _vm._v("."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en72",
      "id": "en72r"
    }
  }, [_vm._v("51")])]), _vm._v(" As Boston merchants and ship captains began trading in Suriname in the eighteenth century, albeit illegally, finding slaves who were familiar with European slavery and could more easily learn English must have been an enticing investment."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en73",
      "id": "en73r"
    }
  }, [_vm._v("52")])]), _c('a', {
    attrs: {
      "id": "anchor76"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor77"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor78"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor79"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor80"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Quaco’s experience in Suriname would have been markedly different from Mark’s in Barbados. Although there was a small creole population in the Dutch colony, the slaves of Suriname were overwhelmingly African. The equatorial climate created a disease environment that killed off large numbers of all settlers, black and white. The brutal plantation regime exacerbated slave deaths. One of biggest differences between Suriname and Barbados is that the latter is a tiny island, while the Dutch colony was in the middle of the rain forest. Slaves could easily escape from plantations, retreat into the hinterland, and form their own communities. By 1700, there were more than one thousand of these so-called maroons in the colony, making up over 10 percent of the population."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en74",
      "id": "en74r"
    }
  }, [_vm._v("53")])]), _vm._v(" They posed an imminent threat to Suriname’s society, either by raiding plantations for supplies and women or by enticing other slaves to run away. Ever vigilant, Dutch authorities kept a close eye on slaves and meted out brutal punishments for the slightest offense. Upon first arriving in the colony to help put down a maroon rebellion, the Scottish-Dutch mercenary John Stedman saw a young woman “in chains, simply covered with a rag around her loins, which was, like her skin, cut and carved by the lash of the whip in a most shocking manner.” After the slave woman failed to fulfill her “task,” Stedman ambiguously mentioned, she was sentenced to receive “200 lashes and for months to drag a chain several yards in length, one end of which was locked to her ankle and at the other end of which was a weight of three score pounds.” Captivated by both the horror and the woman’s beauty, Stedman sketched an image of her (see figure 1)."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en75",
      "id": "en75r"
    }
  }, [_vm._v("54")])]), _vm._v(" Such an excessive punishment for such a minor offense indicates the paranoia of the master class and a willingness to sacrifice troublesome slaves to protect the plantation colony. Such high attrition through disease, absconding, and brutality ensured the constant arrival of new slaves from Africa."), _c('a', {
    attrs: {
      "id": "anchor81"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor82"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Despite swift, vicious responses to affronts against whites, Surinamese planters and Dutch officials gave slaves considerable leeway in policing themselves, most likely to create an illusion that slaves controlled their own communities and would thus prevent others from running away to the maroons. Moreover, given the predominance of blacks and relative low number of whites, especially in the plantation zone outside "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p28"
    }
  }), _vm._v("the colony’s capital, Paramaribo, slaves arbitrated legal matters themselves to ensure swift justice without cases having to go through the Dutch bureaucracy. It was also probably how Quaco landed himself in so much trouble and was sold out of the colony in the first place. Three different groups administered justice on Suriname’s plantations. The first, enslaved drivers, known in Dutch as "), _c('i', [_vm._v("bassia")]), _vm._v(", were slaves entrusted by the master class to oversee workers in the fields. They were arbiters by design, mitigating the abuses of the master class while ensuring that slaves labored. Skilled slaves made up the second group. These slaves could be Africans or creoles, male or female, and held positions of power on the basis of their skills and supposed trustworthiness. Finally, traditional African spiritual leaders and healers, similar to the Obeah of Barbados and known as diviners, like their counterparts in the English Caribbean, exercised considerable influence over the slave community."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en76",
      "id": "en76r"
    }
  }, [_vm._v("55")])]), _vm._v(" This triad held meetings among themselves and adjudicated disputes on the plantation. They also decided what information about various offenses the master class and local court, called the Court of Policy and Criminal Justice, would hear. For minor offenses such as slandering a fellow slave, the "), _c('i', [_vm._v("bassia")]), _vm._v(", artisans, and diviners would arbitrate and mete out punishment themselves. More serious offenses, however, would be passed along to the white community, where the cases would be heard by the Court of Policy and Criminal Justice."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en77",
      "id": "en77r"
    }
  }, [_vm._v("56")])]), _c('a', {
    attrs: {
      "id": "anchor83"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor84"
    }
  })]), _vm._v(" "), _c('div', {
    staticClass: "figure"
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "600",
      "src": "Images/HardestyFig1.jpg",
      "width": "407"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figcap",
    attrs: {
      "id": "Fig1"
    }
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p27"
    }
  }), _vm._v("Figure 1. “A Female Negro Slave, with a Weight chain,” from John Gabriel Stedman, "), _c('i', [_vm._v("Narrative of a Five Years’ Expedition; against the Revolted Negroes of Surinam, in Guiana, on the Wild Coast of South America; from the Year")]), _vm._v(" . . . (London, 1796)")])]), _vm._v(" "), _c('p', [_vm._v("One of the most taboo crimes that a slave could commit, in the eyes of Europeans, slaves, and maroons, was poisoning. Long-standing African traditions dictated that poisoners, often thought to be practicing malfeasant magic, were to be punished with a cruel death or by being sold into slavery. Maroons brutally mutilated the offender and burned the body, while white authorities punished poisoners with death."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en78",
      "id": "en78r"
    }
  }, [_vm._v("57")])]), _vm._v(" Quaco, if Boar’s story is to be believed, was found guilty of poisoning along with the latter’s mother. While it is unclear whom Quaco poisoned, it was most likely not a white person. Unlike Boar’s mother, Quaco did not receive the death penalty. This sentence would have been guaranteed, especially given Surinamese planters’ penchant for using savage displays of violence to make an example, for any slave even accused of poisoning a white person. Instead, poisoning seemed to be an interpersonal crime, one used by slaves against one another, perhaps over a grievance, such as jealousy or a spurned lover."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en79",
      "id": "en79r"
    }
  }, [_vm._v("58")])]), _vm._v(" Lending credence to this accusation, the owners of Boston (the slave poisoned by Quaco), James and Sarah Gardner, testified that Boston took sides with a slave named Sambo, after Sambo and Quaco “had Quarrelled and "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p29"
    }
  }), _vm._v("fought about a Negro woman they were acquainted with.” According to the Gardners, some of Sambo’s hogs had died mysteriously, giving them “Great reason to believe” Quaco poisoned the pigs “in revenge to Sambo.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en80",
      "id": "en80r"
    }
  }, [_vm._v("59")])]), _vm._v(" Given Quaco’s willingness to poison over a mate once, it is not a stretch to think he had done something similar in Suriname. Although his circumstances were unique in Boston, Quaco’s case in Suriname, after being turned over by fellow slaves to the Court of Policy and Criminal Justice, would not have been unusual at all, as 36 percent of all cases heard by the court between 1730 and 1750 involved poisoning."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en81",
      "id": "en81r"
    }
  }, [_vm._v("60")])]), _vm._v(" Quaco, then, hailed from a colony where slaves not only instituted their own system of justice but also commonly resorted to poisoning as a way of fulfilling their various agendas, something he took with him and later deployed in Boston."), _c('a', {
    attrs: {
      "id": "anchor85"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor86"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor87"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor88"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("After Quaco was accused by his fellow slaves and white authorities of poisoning in Suriname, the court condemned him to the traditional African punishment, being sold away as a slave, which is fitting because he was most likely born in Africa and not creole. His African day name suggests as much. More suggestive, however, is to compare his position with the relatively small number of native-born slaves residing in Suriname. Quaco remained enmeshed in a culture in which African norms, traditions, and supernatural forces—poisoning was considered a spiritual act—were part of everyday life. Creoles, however, lived at least part-time within the dominant European culture. "), _c('i', [_vm._v("Bassias")]), _vm._v(" and enslaved artisans tended to be creoles whom whites called on to protect the colony from the threat of maroons or recalcitrant slaves. Masters and white plantation managers commonly armed enslaved craftsmen and had them help track runaways."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en82",
      "id": "en82r"
    }
  }, [_vm._v("61")])]), _vm._v(" Some creoles foreswore their African heritage altogether, fully embracing Dutch colonial society. Full-blooded Africans and mixed-race creoles understood the advantages of accommodating the planter class, especially females. As John Stedman observed, most “gentlemen . . . have a female slave (mostly a Creole) in their keeping, who preserves their linens clean and decent, dresses their victuals with skills, carefully attends them,” and performed other wifely duties. “These girls . . . ,” the mercenary continued, “naturally pride themselves in living with a European, whom they serve with as much tenderness, and to whom they are generally faithful, as if he were their lawful husband.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en83",
      "id": "en83r"
    }
  }, [_vm._v("62")])]), _vm._v(" Stedman himself eventually fell in love with a mixed-race creole woman named Joanna (figure 2). Unlike the women Stedman documented or the plantation creoles who held positions of power, Quaco, by his African birth, was at a distinct disadvantage when "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p31"
    }
  }), _vm._v("attempting to navigate European society, which resulted in him in being sold out of the colony."), _c('a', {
    attrs: {
      "id": "anchor89"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor90"
    }
  })]), _vm._v(" "), _c('div', {
    staticClass: "figure"
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "600",
      "src": "Images/HardestyFig2.jpg",
      "width": "410"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figcap",
    attrs: {
      "id": "Fig2"
    }
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p30"
    }
  }), _vm._v("Figure 2. “Joanna,” from John Gabriel Stedman, "), _c('i', [_vm._v("Narrative of a Five Years’ Expedition; against the Revolted Negroes of Surinam, in Guiana, on the Wild Coast of South America; from the Year")]), _vm._v(" . . . (London, 1796)")])]), _vm._v(" "), _c('p', [_vm._v("That said, however, there were plenty of opportunities for African-born slaves in Suriname to better their position, and the fact that Quaco did not suggests he was intransigent and unwilling to work with those who enslaved him. Although most slave artisans tended to be creole, a number of them were African, either bought as children from Africa and trained on the plantation or already knowing a trade when they arrived. Like their native-born counterparts, they would have participated in tracking down runaways. Surinamese authorities also deployed a free black militia to help fight against the maroons. These were African slaves who had been recruited and manumitted by the colony to fight. Stedman fought alongside a company of these so-called Rangers, generally having a good opinion of them, declaring, none “were accepted but such as were reputed to be of a very good character, and indeed they have since in my own presence given astonishingly proofs of their fidelity to the Europeans and their valor against the revolters.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en84",
      "id": "en84r"
    }
  }, [_vm._v("63")])]), _vm._v(" Stedman, like on other occasions, took the time to sketch one of these free black militiamen (figure 3), noting that the soldier was “Coromantyn,” or Coromantee, an ethnic group from the Gold Coast of Africa. Since Quaco was a slave, he was obviously not a militia member, and there is no evidence to suggest he was a craftsman. Instead, he resisted his enslavement using traditional African methods, not accommodating whites. To say this, however, is not, of course, to lionize Quaco. He was most likely a murderer, someone who resorted to poisoning to resolve disputes. Whether Quaco would have been a serial poisoner had he stayed in Africa we will never know. Nevertheless, it is obvious that the stress of enslavement, in both Suriname and Boston, made killing another human being through the use of poison a strategy that made sense to him."), _c('a', {
    attrs: {
      "id": "anchor91"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Both Quaco and Mark are representatives of a particular class of slaves. They were either natives of or spent time in European New World colonies. Boston merchants and slave owners preferred these slaves, as they spoke English and were thought to be more docile and content with their enslavement. These slaves were anything but submissive, however. We have to look no further than the documents pertaining to these men to understand this. While there were certainly limits to the autonomy obtained by Mark and Quaco—both men eventually ran afoul of the law and were executed by the state—what we see instead of obedient bondsmen and bondswomen are "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p33"
    }
  }), _vm._v("slaves equipped to navigate enslavement. They had already been slaves in European colonies and understood how to exploit the customs and institutions of those societies to better their own lives. Despite their exposure to Euro-American norms, slaves like Mark and Quaco could also call on their African pasts, which connected them to other enslaved Bostonians, to form tight-knit communities and attempt to resist the degradation of enslavement.")]), _vm._v(" "), _c('div', {
    staticClass: "figure"
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "600",
      "src": "Images/HardestyFig3.jpg",
      "width": "404"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figcap",
    attrs: {
      "id": "Fig3"
    }
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p32"
    }
  }), _vm._v("Figure 3. “A Coromantyn Free Negro, or Ranger, armed,” from John Gabriel Stedman, "), _c('i', [_vm._v("Narrative of a Five Years’ Expedition; against the Revolted Negroes of Surinam, in Guiana, on the Wild Coast of South America; from the Year")]), _vm._v(" . . . (London, 1796)")])]), _vm._v(" "), _c('p', {
    staticClass: "psec"
  }, [_vm._v("Although New World creoles and Africans who spent a considerable amount of time in the Americas formed an important component of Boston’s enslaved population, they also mingled with slaves who came directly from Africa. Some of these slaves had a short layover in Barbados, Jamaica, or other British West Indian islands, but it was not enough time to make a significant cultural impact. These “new negroes” came from all over Africa, even from as far away as Madagascar, but they mostly came from West Africa, especially Senegambia, Upper and Lower Guinea, and the Gold Coast."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en85",
      "id": "en85r"
    }
  }, [_vm._v("64")])]), _vm._v(" Remarkably, slaves from this part of Africa would have been culturally equipped to decode Euro-American society in Boston. First, the region had considerable contact with Islam, a religion with some similarities to Protestantism. Likewise, traditional West African cultures, like European societies, also emphasized dependence as a way of binding society together and for children to learn the requisite skills for adulthood. Finally, and most importantly, West Africans were members of a society where slavery was firmly entrenched, a permeable institution where the objective was not freedom but integration into the enslaver’s community."), _c('a', {
    attrs: {
      "id": "anchor92"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("To better understand the Africans who arrived in Boston, we need to examine the regions they predominantly came from. New England slave traders traded mostly with the northern part of West Africa. In 1760, the Boston merchant Timothy Fitch gave Peter Gwinn, the captain of Fitch’s ship the "), _c('i', [_vm._v("Phillis")]), _vm._v(", specific instructions for procuring slaves."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en86",
      "id": "en86r"
    }
  }, [_vm._v("65")])]), _vm._v(" The captain was to sail to the West Coast of Africa, “Touching First at Senegall” and going to the slave “Facktory” there to sell some of his cargo—Fitch was hoping to double his returns—and buy slaves. Fitch hoped Gwinn could “Sell the whole of [his] Cargo thare to a Good Proffett & take Slaves & Cash & Cum Directly Home that would Shorten the Voyage Much,” but this was “not Very Likely to be the Case.” If he could not fill his hold with slaves and sell his cargo in Senegal, appropriately part of a region of Africa known as Senegambia, "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p34"
    }
  }), _vm._v("Gwinn was to sail south to Sierra Leone, part of a region contemporaneously known as Upper Guinea. There, Fitch directed Gwinn, “make the best Trade you Can from place to place till you have disposed of all your Cargo & purcha[sed] your Compleat Cargo of Young Slaves which I sopose wil be about 70 or Eighty More or Less.” If, and only if, the captain could still not procure enough slaves, he was to proceed to Portuguese-controlled central Africa. Otherwise, Gwinn was to stay away from that region and proceed to the Caribbean as quickly as possible to sell his human cargo."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en87",
      "id": "en87r"
    }
  }, [_vm._v("66")])]), _vm._v(" Of the ten known slave-trade voyages Fitch commissioned, most of them captained by Gwinn, eight started in Senegal and worked their way south. The other two commenced on the Windward Coast, also known as Lower Guinea, and just south of Sierra Leone. One of these, in 1762, only started there to avoid Spanish privateers hanging around the Canary Islands."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en88",
      "id": "en88r"
    }
  }, [_vm._v("67")])]), _vm._v(" As Fitch demonstrates, most New England slave traders preferred to trade for slaves on the northern edge of West Africa, and many Bostonians purchased slaves from that region."), _c('a', {
    attrs: {
      "id": "anchor93"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor94"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor95"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Besides slave-trade merchants’ letters, other documents indicate the African birthplaces of enslaved Bostonians. Slave-for-sale advertisements provide some of this information, but they need to be examined with one caveat. Before the 1740s, very few ads noted where in Africa a slave was from but instead noted only from where in the Americas the slave ship that brought them was arriving. Nevertheless, between 1704 and 1781, newspapers published for-sale notices for 128 African slaves and another 225 under the ambiguous moniker of “lately Arrived” or “New Arrival,” suggesting a bondsman or bondswoman freshly arrived from Africa."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en89",
      "id": "en89r"
    }
  }, [_vm._v("68")])]), _vm._v(" Of these, some were quite specific, listing the Guinea Coast, the center of the New England slave trade, as the place of origin. Robert Ball, a slave-ship captain, advertised a “Parcel of likely young Negro Boys and Girls” from the “Coast of Guinea” in 1744."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en90",
      "id": "en90r"
    }
  }, [_vm._v("69")])]), _vm._v(" Others noted the exact region, such as one that promoted a “Number of Prime Slaves from the Windward Coast.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en91",
      "id": "en91r"
    }
  }, [_vm._v("70")])]), _vm._v(" While none of the advertisements acknowledged the specific place or port of departure for slaves, it is obvious that most slaves originated from one swath of West Africa."), _c('a', {
    attrs: {
      "id": "anchor96"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor97"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor98"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("By examining the slave trade in this part of Africa, we can also discern the primary ethnic groups that the inhabitants of Senegambia captured and sold to English slave traders. Most were from the immediate vicinity, such as the Bambara people, located to the east in the middle Niger River valley. Two Bambara states in particular, Segu "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p35"
    }
  }), _vm._v("and Kaarta, were the targets of slave raids. Like other Senegambians, the Bambara were practicing Muslims and adhered to a strict caste system consisting of nobility, vassals, and slaves. In addition, local Wolof—a coastal Islamic group—kingdoms often preyed on their own subjects to sell to the Europeans. These included local non-Muslims subject to Wolof jurisdiction, such as the Sereer, but they also sold their own hereditary slaves."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en92",
      "id": "en92r"
    }
  }, [_vm._v("71")])]), _vm._v(" While these groups were just a few of hundreds of language and ethnic groups that inhabited this region, the historian John Thornton has demonstrated that West Africa contained just two broad cultural groups, one in Upper Guinea inhabiting the region from modern Senegal to Liberia and the other in Lower Guinea, which stretched from the Ivory Coast to Cameroon."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en93",
      "id": "en93r"
    }
  }, [_vm._v("72")])]), _vm._v(" These larger regions shared two common institutions: traditional forms of dependence and slavery. Before exploring those, however, we need to examine Islam, which predominated in Upper Guinea, and its influence on slavery in Boston."), _c('a', {
    attrs: {
      "id": "anchor99"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor100"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Although relatively limited in other parts of West Africa, Islam had taken root in Senegambia, the northern part of Upper Guinea, as early as the tenth century. Prior to the arrival of Europeans, the Muslim Mali Empire controlled most of Upper Guinea. When the empire fell, its people, known as the Mandé, spread throughout Upper and Lower Guinea, carrying Islam with them. European travelers to West Africa noted the presence of Mandé, especially the Mandinka (Mandingo) subgroup. John Matthews, a lieutenant in the Royal Navy in Sierra Leone during the 1780s, encountered some Mandinkas, who boasted “Mahomet himself [could not] have wished for more zealous promoters of his law.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en94",
      "id": "en94r"
    }
  }, [_vm._v("73")])]), _vm._v(" While surveying the Gambia River for the Royal African Company in the 1740s, the Englishman William Smith encountered African Muslims, who he noted were “strict in the external Observance of their Religious Ceremonies,” although they drank alcohol in private."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en95",
      "id": "en95r"
    }
  }, [_vm._v("74")])]), _c('a', {
    attrs: {
      "id": "anchor101"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor102"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Both Matthews’s and Smith’s discussions suggest there was a high probability of New England slave traders purchasing Muslim slaves and selling them in Boston. The historian Michael Gomez argues that names may be indicative of Muslim slaves. The name “Sambo” for example, may come from the Pulaar word for “second son.” Pulaar is the language of the Fulani, a predominantly Muslim West African ethnic group."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en96",
      "id": "en96r"
    }
  }, [_vm._v("75")])]), _vm._v(" One name that was unmistakably Islamic was “Mahomet,” the name of at least one Bostonian. In 1743, the Suffolk County Court of Common Pleas was set to hear the case "), _c('i', [_vm._v("Pasmore v.")]), _vm._v(" "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p36"
    }
  }), _c('i', [_vm._v("Mahomet")]), _vm._v(", but the defendant, Bashaw Mahomet, failed to appear—hence we do not have many details about the case. There are a couple pieces of evidence, however, that allow us to look into his life. The court noted he was a mariner, possibly explaining his absence from court. Moreover, Mahomet, the court readily acknowledged, was a resident of Boston."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en97",
      "id": "en97r"
    }
  }, [_vm._v("76")])]), _vm._v(" We know more about Roger Pasmore, the plaintiff, and he might provide a few more clues about Mahomet. Pasmore, like the African, was a sailor, and in 1740, he testified about a smallpox outbreak onboard the ship he was traveling on from Bristol, England, to Boston."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en98",
      "id": "en98r"
    }
  }, [_vm._v("77")])]), _vm._v(" He was also charitable, housing foreign sailors and one who was injured."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en99",
      "id": "en99r"
    }
  }, [_vm._v("78")])]), _vm._v(" More important for his relationship with Mahomet, however, was his religion. Pasmore married Mary Read in July 1736, and the Reverend Elisha Callendar, the minister of Boston’s First Baptist Church, officiated."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en100",
      "id": "en100r"
    }
  }, [_vm._v("79")])]), _vm._v(" While sailors were a quarrelsome lot and would fight and sue one another over minor issues, religion may have been a factor in whatever dispute arose between Pasmore and Mahomet. Pasmore, a likely participant in George Whitefield’s 1740 evangelical revival in Boston, would have been deeply offended had Mahomet openly practiced his religion, especially if he behaved like the Muslim slave in Pennsylvania who introduced himself by opening with “Allah. Muhammad.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en101",
      "id": "en101r"
    }
  }, [_vm._v("80")])]), _vm._v(" That alone was not enough for Pasmore to file a civil suit, but Mahomet’s faith was probably odious enough that Pasmore may have opened litigation he otherwise might not have. As the case of Mahomet and Pasmore suggests, Muslim Africans resided in Boston, and their religion was almost certainly a driving factor in shaping their lives and interactions."), _c('a', {
    attrs: {
      "id": "anchor103"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor104"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor105"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor106"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor107"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor108"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("The question remains how Islamic slaves would have adjusted to their new setting."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en102",
      "id": "en102r"
    }
  }, [_vm._v("81")])]), _vm._v(" Despite deep theological differences, Muslim slaves would have shared some values with their Protestant masters. Both religions emphasized education and literacy, and practitioners were “people of the book.” As we will later see, ministers in Massachusetts exhorted slave owners to proselytize their slaves by teaching them to read and write. The Puritan minister Cotton Mather even opened a school for slaves to learn to read and published a catechism for slaves."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en103",
      "id": "en103r"
    }
  }, [_vm._v("82")])]), _vm._v(" Like Christians in Boston, West African Muslims attempted to bring others into their faith. While observing the Mandinka people, Matthews recorded how they spread into surrounding villages, where they would “erect schools, and teach their youth gratis, to read and write Arabic.” These schools proved to be part of a successful strategy of increasing Islam’s influence in the region, as in almost every village "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p37"
    }
  }), _vm._v("where the Mandinka established a school, they also gained the “confidence of the chiefs and principal people” and even held positions of authority. One such position was what Matthews called a “"), _c('i', [_vm._v("bookman")]), _vm._v(",” most likely the village record keeper."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en104",
      "id": "en104r"
    }
  }, [_vm._v("83")])]), _vm._v(" The tradition of reading and writing in Islam, like that in Reformed Protestantism, was a source of empowerment, one to be tapped in times of crisis. Islamic slaves in Boston would have had a special appreciation for literacy and possibly inspired them to learn to read and write English. It is no wonder that one of the first enslaved Bostonians—and African Americans—to publish, Phillis Wheatley, was born in one of the most heavily Islamized regions of sub-Saharan Africa, Senegambia."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en105",
      "id": "en105r"
    }
  }, [_vm._v("84")])]), _c('a', {
    attrs: {
      "id": "anchor109"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor110"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor111"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor112"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("If Islam was relatively limited in the part of Africa under examination, almost all peoples living in West Africa lived in societies bound together by ties of dependence. As in early modern European cultures, dependency was ingrained in the very fabric of society. This dependence in both places was part of a child’s transition to adulthood, in which they learned valuable lifelong skills, and was not a pejorative state of helplessness. Dependence in West Africa, however, differed from that in Europe and came from local notions of wealth and land tenure. In Europe, land and the ownership of it was the basis of all wealth. For Africans, a village or another corporate body such as a monarch controlled all of the land. The monarch or village elders would dole out land to those who were willing to cultivate it. But land in itself could not provide wealth; only the products of the land generated income. To produce those products, those who were given land needed labor. For that, they turned to unfree labor including, as we will see, slavery."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en106",
      "id": "en106r"
    }
  }, [_vm._v("85")])]), _vm._v(" Dependency did not allow landholders to own labor like slavery did, but it did allow them to control it. One form of dependency was marriage, in which wives, as was customary in Africa, were under the control of their husbands, who also commanded their labor. In such a world, polygamy made sense, as more wives meant a larger labor pool to draw on. Not only did African women perform the traditionally female roles of rearing children and tending crops, but they were also involved in other forms of production. A monarch of the Kingdom of Whydah, an area slightly east of where enslaved Bostonians came from, allegedly had over one thousand wives, many of whom made cloth for export."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en107",
      "id": "en107r"
    }
  }, [_vm._v("86")])]), _c('a', {
    attrs: {
      "id": "anchor113"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor114"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Similar to the use of wifely labor, other forms of African dependency would have been familiar to Europeans. When the nearly seventy-year-old Venture Smith sat down to relate his life story to a "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p38"
    }
  }), _vm._v("Connecticut schoolteacher in 1798, he vividly recalled his boyhood near the Gold Coast of West Africa. Smith, a nearly mythical figure in his own lifetime and one who continues to fascinate scholars to this day, was one of the few New England slaves who was born in Africa and lived long enough to find freedom in the United States. For all intents and purposes, before Smith was kidnapped, enslaved, and sent across the Atlantic, his experience in Africa was relatively conventional, including an event from his early childhood. When Smith was around five or six years old, his mother had a quarrel with his father over the latter’s marriage to a third wife. While the couple later reconciled, she left her husband for a time, heading “eastward” with her three children, of whom Venture was the oldest. After traveling for a few days, Venture and his family encountered a “very rich farmer,” and his mother left him with the farmer, “separate from all [his] relations and acquaintance.” After his mother left, his “new guardian,” who Venture often called his master, taught him the “business of tending sheep.” Essentially, Smith’s mother apprenticed him to a wealthy farmer, from whom he learned the basics of husbandry."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en108",
      "id": "en108r"
    }
  }, [_vm._v("87")])]), _c('a', {
    attrs: {
      "id": "anchor115"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Venture Smith, like so many English boys his age, entered into a dependent relationship to learn a skill. His master also fulfilled the obligations of those who took on apprentices, taking care of Smith. When two dogs attacked Smith and grievously wounded him, leaving him permanently scarred, his master intervened, “relieved” Smith of the attacks, carried the boy home, and nursed him back to health. Smith’s apprenticeship would not have been as formalized as a European one, which was usually forged with a written contract, and his servitude was over when his “father sent” for him to “return home.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en109",
      "id": "en109r"
    }
  }, [_vm._v("88")])]), _vm._v(" Dependence was an important cultural value that bound West Africans together, ensured a steady supply of labor, and taught children the requisite skills to become adults. When these Africans entered Boston, they would have found a world where dependence similarly regulated social and economic relations."), _c('a', {
    attrs: {
      "id": "anchor116"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Despite the significance of dependency in African society, it did not allow African farmers and large landholders to command labor like slavery did. As such, slavery was in many ways the ultimate form of dependence, making slaves completely reliant on their masters for their survival and standing in the community, while ensuring that their owners had a steady supply of workers. Yet African conceptions of wealth as labor meant slaves were also the “most important avenue for private, reproducing wealth available to Africans.” Unlike "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p39"
    }
  }), _vm._v("Europeans, who bought land when looking to make money, Africans purchased slaves, which generated wealth and, as property, represented opportunities to increase one’s own prosperity and could be passed on as part of an African man’s patrimony."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en110",
      "id": "en110r"
    }
  }, [_vm._v("89")])]), _vm._v(" As the only secure form of revenue-generating property, slavery became ingrained in the social fabric of most West African societies. Traditional law often condemned those who were convicted of major crimes to either death or slavery. As John Matthews observed, the “crimes of murder, poison, witchcraft, adultery, and theft, are always as capital, and have been punished with either death or slavery from time immemorial.” In many instances, offenders had their death sentence commuted and were sold into slavery instead."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en111",
      "id": "en111r"
    }
  }, [_vm._v("90")])]), _vm._v(" Even hardened criminals were potential workers in a society in which labor equaled wealth."), _c('a', {
    attrs: {
      "id": "anchor117"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor118"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("A large percentage of premodern Africa’s population were slaves. While the numbers for the eighteenth century are next to impossible to discern, West Africa’s use of slave labor was well recorded at later times. Some of the best statistics regarding this phenomenon are from Senegambia. In that region, late eighteenth-century travelers estimated that upward of three-quarters of the Mandinka states’ population was enslaved. In a more recent study of the Fulbe people, anthropologists found that nearly 20 percent of the current population were the descendants of slaves. Finally, in 1906, French colonial authorities estimated that roughly 25 percent of all the people in French West Africa were slaves."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en112",
      "id": "en112r"
    }
  }, [_vm._v("91")])]), _vm._v(" What these wildly different numbers suggest is that slavery existed nearly everywhere in Africa, but there were higher numbers of slaves in some places and next to none in others. This would have been a landscape of slavery similar to the one encountered in the Americas, with some regions, like the Caribbean, Brazil, and the American South, having large concentrations of slaves, and other places, like New England and the Rio de la Plata, having a smaller percentage of their population enslaved."), _c('a', {
    attrs: {
      "id": "anchor119"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Despite demographic similarities with the Americas, slavery in Africa could be radically different from the institution in the Western Hemisphere. For starters, not all slaves were engaged in agriculture and artisanal enterprises. Many slaves served as bureaucrats and military officers, as they “were an ideal form of loyal workers, soldiers, and retainers,” in multiethnic, composite states tenuously held together by conquest and alliance. Indeed, some African empires used entire armies and bureaucracies composed of slaves in order to check the power of the local nobility, often composed of the former autonomous rulers of the region."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en113",
      "id": "en113r"
    }
  }, [_vm._v("92")])]), _vm._v(" "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p40"
    }
  }), _vm._v("Even more familiar forms of servitude, such as agricultural slavery, were radically different in Africa. Given the nature of African land tenure, in which any person willing to cultivate a tract of land was given permission to use it, most slaves in husbandry tended to be like peasants or tenant farmers elsewhere. They worked either on their own or with other slaves on secluded plots of land, what Matthews called “slave towns,” in many cases working one day a week for themselves and the rest for their masters."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en114",
      "id": "en114r"
    }
  }, [_vm._v("93")])]), _vm._v(" As long as slaves recognized their dependent status, they were given quite a bit of leeway and elevated into trusted positions."), _c('a', {
    attrs: {
      "id": "anchor120"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor121"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("All of this is not to suggest that slavery was somehow more benign in Africa; but it was radically different, and those differences allowed African-born slaves to approach slavery in unique ways. African societies defined themselves through, as noted earlier, chains of dependence and kinship networks. Slaves lacked ties to the community and were outsiders, and their sole social function was to provide labor. In many societies, however, slaves had the opportunity to join their captors’ society."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en115",
      "id": "en115r"
    }
  }, [_vm._v("94")])]), _vm._v(" Matthews and Smith both observed this firsthand. Smith noted how African law allowed fathers to legitimate children born by his slave(s); if he did not, however, “his Heir” would look on these children as slaves, “and treat [them] as such.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en116",
      "id": "en116r"
    }
  }, [_vm._v("95")])]), _c('a', {
    attrs: {
      "id": "anchor122"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor123"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("Matthews, for his part, saw differences between the various types of slavery. Those employed in agriculture were “held in no higher estimation than any other animal that contributes to its cultivation,” while the “house slave” was “considered as a branch of the family.” The latter also assumed their “master’s name and calls him father.” Yet Matthews could never understand the behavior of slaves in Africa because, unlike in the Americas, the enslaved did not seem to resist their enslavement. Instead, as slaves knew “no other situation” than slavery, they were “indifferent” as long as they received the “necessaries of life.”"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en117",
      "id": "en117r"
    }
  }, [_vm._v("96")])]), _vm._v(" While Matthews, as an outsider, was not equipped to fully comprehend slavery in Africa, the traveler also missed the point of African slavery, in which even lowly agricultural slaves had certain rights, such as not being able to be sold except for serious offenses, and having considerable autonomy and mobility, ownership of at least some of their own time, the ability to marry, and even the opportunity to become trusted members of the community."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('a', {
    staticClass: "enref",
    attrs: {
      "href": "#/Text/Hardesty0016#en118",
      "id": "en118r"
    }
  }, [_vm._v("97")])]), _vm._v(" Under such conditions, the ultimate form of resistance was for slaves to overcome their outsider status and integrate themselves into their masters’ society."), _c('a', {
    attrs: {
      "id": "anchor124"
    }
  }), _c('a', {
    attrs: {
      "id": "anchor125"
    }
  })]), _vm._v(" "), _c('p', [_vm._v("The Africans who arrived in Boston were primarily from West Africa, an area comprising Senegambia, Upper and Lower Guinea, "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p41"
    }
  }), _vm._v("and the Gold Coast. They brought with them a number of cultural traditions and institutions, the most important of which were Islam, dependence, and slavery. Each of these equipped Africans to navigate slavery in Boston, especially African concepts of slavery, in which slaves would attempt to incorporate themselves into their owners’ community. When these Africans arrived in Boston, they did not bring with them notions of resistance built on abstract ideals such as emancipation but brought traditions rooted in their African past, in which assimilation was more important than freedom.")]), _vm._v(" "), _c('p', {
    staticClass: "psec"
  }, [_vm._v("Enslaved Bostonians had diverse, polyglot, and cosmopolitan origins. Throughout the seventeenth century, New English colonists purchased a small number of slaves, whose descendants constituted a small number of enslaved Bostonians. While this first generation of slaves lived lives relatively similar to those of their eighteenth-century counterparts, even employing some of the same strategies that later slaves found successful, such as using the law, there were never more than two or three hundred in Boston. It was the town’s integration into Atlantic slave-trade networks that caused a demographic explosion in the eighteenth century. Between 1700 and 1750, the enslaved population of Boston increased nearly eight times, and these new arrivals came from a variety of places in Africa and the Americas. American creoles were a significant component of this new population, either having been born in other parts of the Americas or having lived there for a considerable time. Two of these slaves, Mark, born in Barbados, and Quaco, from Suriname although most likely born in Africa, exemplify how familiarity with enslavement in the Americas helped them to carve out a space for themselves once they arrived in Boston. Like the American creoles, African slaves came from cultures deeply embedded in a tradition of slavery. Although they brought other experiences, such as Islam and dependence, with them, African notions of bondage largely shaped how they interacted with the institution of slavery and learned how to navigate it.")]), _vm._v(" "), _c('p', [_vm._v("Origins mattered. Each of the groups explored earlier brought cultural baggage that equipped them to better navigate slavery in Boston. Boston’s enslaved population was an Atlantic community, an assortment of peoples with diverse backgrounds and experiences. They channeled their collective pasts into creating a better life for themselves once they were in Boston. Understanding these histories is vital to understanding the lives of slaves. As almost all people of African "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p42"
    }
  }), _vm._v("descent in Boston had experienced slavery firsthand or came from societies where enslavement was part of the social fabric, we are forced to rethink what slavery and freedom meant to the enslaved. Freedom was amorphous, abstract, fickle, and ever changing, while slavery was deeply embedded in their own experience, something to be easily navigated and challenged. To say this is not to say that slaves wanted to be slaves but that these were people well equipped to deal with the pressures of slavery. As recent arrivals to a society undergoing its own cultural transformation, becoming a place where slavery, dependence, and unfreedom structured everyday life, the enslaved would need to muster all these skills to navigate Boston’s peculiar brand of slavery.")])])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-dbafc304","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/Text/Hardesty-0010.vue
// module id = 81
// module chunks = 0